/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Granite Bagas
*/

import React, { useState, useEffect, useRef } from 'react';
// eslint-disable-next-line no-unused-vars
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import Toast from 'react-native-toast-message';
import { View, StyleSheet, ScrollView, Dimensions } from 'react-native';
import * as Yup from 'yup';

import SociollaText from '@components/sociolla/SociollaText';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import * as Layout from '@components/screen/Layout';
import CustomerSearch from '@components/form/search/CustomerSearch';
import {
  checkDetailAddress,
  findMatchValue,
  defaultCountry,
} from '@global/helpers';
import {
  SociollaInput,
  SociollaSelectBox,
  SociollaCheckbox,
  SociollaMapPicker,
  SociollaMarketTypeSearch,
} from '@components/sociolla/form';
import TitleSubSection from '@components/global/TitleSubSection';

import { isWeb, isMobileWeb, isAndroid } from '@global/screens';
import { showPopup } from '@/store/modal';
import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';
import {
  getAllStates,
  getAllCities,
  getCountries,
  createOutlet,
} from '@api/ms-account';
import { MsAccount } from '@api/ms-account';
import { PKP_TYPES, WHT } from '@/constants';

const FormValidationSchema = Yup.object().shape({
  customer: Yup.object().required('Customer is required.'),
  name: Yup.string().required('Outlet Name is required.'),
  code: Yup.string().required('Outlet Code is required.'),
  sales_team: Yup.object().required('Sales Team is required.'),
  market_type: Yup.object().required('Market Type is required.'),
  business_type: Yup.object().required('Business Type is required.').nullable(),
  street: Yup.string().required('Street is required.'),
  state: Yup.object().required('State ID is required.'),
  city: Yup.object().required('City ID is required.'),
  lat_long: Yup.object(),
  tax_name: Yup.string().required('Tax Identification Name is required.'),
  tax_number: Yup.string()
    .required('Tax Identification Number is required.')
    .matches(/^\d{16}$/, 'Number must be a 16 digit number'),
  tax_is_pkp: Yup.mixed().required('Taxable Entrepreneur is required.'),
  tax_wht: Yup.object().required('WHT (Corporate/Personal) is required.'),
});

const dimension = Dimensions.get('screen');

const SubdistributorFormScreen = (props) => {
  const dispatch = useDispatch();
  const formRef = useRef();

  const [detailData, setDetailData] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [customer, setCustomer] = useState();
  const [bussinessType, setBussinessType] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [country, setCountry] = useState();
  const [state, setState] = useState();
  const [city, setCity] = useState();

  const getOutletDetail = useApi(MsAccount.getOutletDetail);

  const getDetail = async (id) => {
    const response = await getOutletDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;

      const cusRes = await MsAccount.getCustomerId(result.customer.id);

      setCustomer(cusRes?.data?.data);
      setState({
        ...result.state,
        _id: result.state.id,
      });
      setCity({
        ...result.city,
        _id: result.city.id,
      });

      const codeArr = result.code.split(`${result.customer.code}-`);

      const resCities = await getAllCities({
        params: {
          filter: {
            state_id: result.state.id,
          },
        },
      });
      if (resCities?.data.data) {
        setCities(resCities.data.data);
      }

      await getMarketType(result.market_type.id);

      const formattedData = {
        ...result,
        id: result._id,
        business_type: {
          ...result.business_type,
          _id: result.business_type.id,
        },
        code: codeArr?.[1] || '',
        tax_is_pkp: PKP_TYPES.find((v) => v.value == result.tax_is_pkp),
        tax_wht: WHT.find((v) => v.value == result.tax_wht),
      };
      setDetailData(formattedData);

      setIsReady(true);
    }
  };

  const submitForm = async (payload, { resetForm }) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      let finalPayload = {
        ...payload,
        customer: {
          ...payload.customer,
          code: customer.customer_code,
          image:
            Array.isArray(customer?.image) && customer.image?.length
              ? customer.image[0]
              : !Array.isArray(customer?.image) && customer?.image
              ? customer.image
              : '',
        },
        code: `${customer.customer_code}-${payload.code}`,
        exact_code: payload.code,
        market_type: {
          ...payload.market_type,
          id: payload.market_type._id || payload.market_type.id,
        },
        business_type: {
          ...payload.business_type,
          id: payload.business_type._id || payload.business_type.id,
          code: payload.business_type.code,
          description: payload.business_type.description,
        },
        country: {
          id: country._id,
          name: country.name,
          cerebro_id: country.odoo_country_id,
        },
        state: {
          ...state,
          id: state._id,
          state: state.name,
          cerebro_id: country.odoo_state_id,
        },
        city: {
          id: city._id,
          name: city.name,
        },
        tax_name: payload.tax_name,
        tax_number: payload.tax_number,
        tax_is_pkp: payload.tax_is_pkp.value,
        tax_wht: payload.tax_wht.value,
      };

      // Filter out null, undefined, and empty string values
      finalPayload = Object.fromEntries(
        Object.entries(finalPayload).filter(
          ([key, value]) =>
            value !== null && value !== undefined && value !== '',
        ),
      );

      if (!isEdit) {
        const createData = await createOutlet(finalPayload);
        if (createData?.data?.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'New outlet has been registered successfully',
            type: 'success',
          });
          resetForm();
          resetData();
          props.navigation.navigate('SubdistributorScreen', {});
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'A system error occurred',
            type: 'error',
          });
        }
      } else {
        const updateData = await MsAccount.updateOutlet(
          detailData._id,
          finalPayload,
        );

        if (updateData?.data?.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'Successfully edited data',
            type: 'success',
          });
          resetForm();
          resetData();
          props.navigation.navigate('SubdistributorDetailScreen', {
            id: updateData?.data?.data?._id,
            titleScreen: updateData?.data?.data?.customer?.name,
            name: updateData?.data?.data?.customer?.name,
          });
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'A system error occurred',
            type: 'error',
          });
        }
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const resetData = () => {
    setDetailData({});
    setIsEdit(false);
    setIsReady(false);
    setCustomer();
    setBussinessType([]);
    setCountries([]);
    setStates([]);
    setCities();
    setCountry();
    setState();
    setCity();
  };

  useEffect(() => {
    props.navigation.addListener('blur', () => {
      resetData();
    });

    return function cleanup() {
      props.navigation.removeListener('blur');
    };
  }, [props.navigation]);

  const goBack = () => {
    resetData();
    if (isEdit) {
      props.navigation.navigate('SubdistributorDetailScreen', {
        id: detailData?._id,
        titleScreen: detailData?.customer?.name,
        name: detailData?.customer?.name,
      });
    } else {
      props.navigation.replace('SubdistributorFormScreen', {});
      props.navigation.navigate('SubdistributorScreen');
    }
  };

  const onChangeMarketTypes = (val) => {
    for (let i = 0; i < val?.business_type?.length; i++) {
      if (val.business_type[i].description) {
        val.business_type[
          i
        ].label = `${val.business_type[i].code} (${val.business_type[i].description})`;
      }
    }
    setBussinessType(val.business_type.filter((bt) => bt.is_active));
  };

  const onChangeState = async (selectedState) => {
    setCity();
    setState(selectedState);
    const resCities = await getAllCities({
      params: {
        filter: {
          state_id: selectedState._id,
        },
      },
    });
    if (resCities?.data.data) {
      setCities(resCities.data.data);
    }
  };

  const getMarketType = async (id) => {
    const res = await MsB2b.getMarketType({
      params: {
        filter: {
          _id: id,
        },
      },
    });
    if (res.status === 200 && res.data?.data?.length) {
      const businessTypeData = res.data.data[0].business_type
        .map((v) => ({
          ...v,
          label: v.description ? `${v.code} (${v.description})` : v.code,
        }))
        .filter((bt) => bt.is_active);
      setBussinessType(businessTypeData);
    }
  };

  const getCountryState = async () => {
    try {
      const resCountries = await getCountries();
      if (resCountries.data?.data) {
        setCountries(resCountries.data.data);
        const defCoun = resCountries.data.data.find(
          (co) => co.name.toLowerCase() == defaultCountry(),
        );
        if (defCoun) {
          setCountry(defCoun);

          const resStates = await getAllStates({
            params: {
              filter: {
                country_id: defCoun._id,
              },
            },
          });
          if (resStates.data?.data) {
            setStates(resStates.data.data);
          }
        }
      }
    } catch (error) {
      console.log('🚀🚀🚀 ~ error:', error);
      Toast.show({
        text1: 'Something Wrong, please try again.',
        type: 'error',
      });
    }
  };

  const fetchData = async () => {
    setIsEdit(false);
    setDetailData({});
    setIsReady(false);
    await getCountryState();
    if (props?.route?.params?.id) {
      setIsEdit(true);
      await getDetail(props.route.params.id);
    } else {
      setTimeout(() => {
        setIsReady(true);
      }, 500);
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      fetchData();
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  const onCurrentLocation = (data) => {
    const cn = checkDetailAddress(countries, data.address?.country || '');
    const state = checkDetailAddress(states, data.address?.state || '');
    if (cn) {
      setCountry(cn);
    }
    if (state) {
      setState(state);
    }
  };

  return (
    <Layout.Container>
      {(isEdit && getOutletDetail?.loading) || !isReady ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          innerRef={formRef}
          initialValues={{
            customer:
              isEdit &&
              detailData?.customer &&
              Object.keys(detailData.customer).length
                ? detailData.customer
                : '',
            name: isEdit && detailData?.name ? detailData.name : '',
            code: isEdit && detailData?.code ? detailData.code : '',
            sales_team:
              isEdit &&
              detailData?.sales_team &&
              Object.keys(detailData.sales_team).length
                ? detailData.sales_team
                : '',
            market_type:
              isEdit &&
              detailData?.market_type &&
              Object.keys(detailData.market_type).length
                ? detailData.market_type
                : '',
            business_type:
              isEdit &&
              detailData?.business_type &&
              Object.keys(detailData.business_type).length
                ? detailData.business_type
                : '',
            street: isEdit && detailData?.street ? detailData.street : '',
            country:
              isEdit &&
              detailData?.country &&
              Object.keys(detailData.country).length
                ? detailData.country
                : '',
            state:
              isEdit &&
              detailData?.state &&
              Object.keys(detailData.state).length
                ? detailData.state
                : '',
            city:
              isEdit && detailData?.city && Object.keys(detailData.city).length
                ? detailData.city
                : '',
            is_active: isEdit && detailData?.is_active ? true : false,
            lat_long: isEdit && detailData?.lat_long ? detailData.lat_long : '',
            tax_name: isEdit && detailData?.tax_name ? detailData.tax_name : '',
            tax_number:
              isEdit && detailData?.tax_number ? detailData.tax_number : '',
            tax_is_pkp:
              isEdit && detailData?.tax_is_pkp ? detailData.tax_is_pkp : false,
            tax_wht: isEdit && detailData?.tax_wht ? detailData.tax_wht : '',
          }}
          validationSchema={FormValidationSchema}
          onSubmit={submitForm}>
          {({
            setFieldValue,
            validateForm,
            handleSubmit,
            errors,
            touched,
            values,
          }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false}>
                  <Layout.Back
                    title="Sub-Distributor"
                    onCallback={() => goBack()}
                  />
                  <Layout.Title
                    title={`${isEdit ? `Edit` : `Register New`} Outlet`}>
                    <View style={styles.row}>
                      <Button
                        type="default"
                        rounded
                        buttonStyle={{ width: 120, marginRight: 10 }}
                        title={
                          <SociollaText
                            type="bold"
                            style={{
                              fontSize: 14,
                              letterSpacing: 0.5,
                            }}>
                            Cancel
                          </SociollaText>
                        }
                        onPress={goBack}
                      />
                      <Button
                        type="primary"
                        rounded
                        buttonStyle={{ width: 120 }}
                        disabledStyle={{ width: 120 }}
                        title={
                          <SociollaText
                            type="bold"
                            style={{
                              fontSize: 14,
                              letterSpacing: 0.5,
                              color: Colors.white,
                            }}>
                            {isEdit ? 'Save' : 'Add'}
                          </SociollaText>
                        }
                        onPress={(evt) => {
                          handleSubmit(evt);
                        }}
                      />
                    </View>
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper rounded>
                <ScrollView
                  style={isMobileWeb ? { height: dimension.height - 85 } : {}}
                  contentContainerStyle={{ width: '100%' }}>
                  <Layout.FormWrapper
                    style={{
                      paddingHorizontal: 15,
                      paddingTop: 20,
                      ...(isWeb
                        ? {
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            paddingHorizontal: 20,
                            marginBottom: 15,
                          }
                        : {
                            paddingBottom: !isMobileWeb
                              ? isAndroid
                                ? 200
                                : 150
                              : 320,
                          }),
                    }}>
                    <View style={styles.rowForm}>
                      <Layout.FormGroup>
                        <CustomerSearch
                          fields="_id name customer_code image sales_team market_type"
                          label="Sub Distributor Name"
                          placeholder="Select Sub Distributor"
                          value={values.customer}
                          isRequired={true}
                          editable={!isEdit}
                          hasError={
                            errors.customer && touched.customer
                              ? errors.customer
                              : null
                          }
                          customFilter={{
                            status: 'approved',
                            is_active: true,
                            is_sub_distributor: true,
                          }}
                          onChange={async (value) => {
                            setCustomer(value);
                            setFieldValue('customer', value, true);
                            setFieldValue('sales_team', value.sales_team, true);
                            setFieldValue(
                              'market_type',
                              value.market_type,
                              true,
                            );
                            setFieldValue('business_type', null, true);
                            await getMarketType(value.market_type.id);
                          }}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SociollaInput
                          name="name"
                          label="Outlet Name"
                          isRequired
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <View style={{ flexDirection: 'row', width: '100%' }}>
                          <View style={{ flex: 1 }}>
                            <SociollaInput
                              label="Outlet Code"
                              value={customer?.customer_code}
                              disabled
                              isRequired
                              editable={false}
                              name="customer_code"
                            />
                          </View>
                          <View
                            style={{
                              marginTop: 38,
                            }}>
                            <SociollaText style={{ fontSize: 16 }}>
                              -
                            </SociollaText>
                          </View>
                          <View style={{ flex: 1, marginTop: 32 }}>
                            <SociollaInput name="code" isRequired label="" />
                          </View>
                        </View>
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <Input
                          label="Sales Team"
                          value={
                            values.sales_team && values.sales_team.id
                              ? values.sales_team.name
                              : ''
                          }
                          isRequired
                          editable={false}
                          hasError={
                            errors.sales_team && touched.sales_team
                              ? errors.sales_team
                              : null
                          }
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SociollaMarketTypeSearch
                          name="market_type"
                          onChange={(val) => {
                            setFieldValue('business_type', null, true);
                            onChangeMarketTypes(val);
                          }}
                          isRequired
                          editable={values['sales_team'] ? true : false}
                          disabled={values['sales_team'] ? false : true}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SociollaSelectBox
                          label="Business Type"
                          options={bussinessType}
                          name="business_type"
                          trackBy="_id"
                          labelKey="label"
                          formLabelKey="code"
                          editable={values['market_type'] ? true : false}
                          disabled={values['market_type'] ? false : true}
                          isRequired
                        />
                      </Layout.FormGroup>
                    </View>
                    <View style={styles.rowForm}>
                      <Layout.FormGroup>
                        <SociollaText
                          type="medium"
                          style={{ fontSize: 16, marginBottom: 10 }}>
                          Address
                        </SociollaText>
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SociollaInput
                          label="Street"
                          name="street"
                          autoComplete="off"
                          multiline={true}
                          numberOfLines={4}
                          isRequired
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SociollaSelectBox
                          value={country}
                          label="Country ID"
                          options={countries}
                          name="country"
                          trackBy="_id"
                          labelKey="name"
                          editable={false}
                          disabled={true}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SociollaSelectBox
                          label="State ID"
                          value={state}
                          options={states}
                          name="state"
                          trackBy="_id"
                          labelKey="name"
                          isRequired
                          onChange={onChangeState}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <SociollaSelectBox
                          label="City ID"
                          value={city}
                          options={cities}
                          name="city"
                          trackBy="_id"
                          labelKey="name"
                          editable={state ? true : false}
                          disabled={state ? false : true}
                          isRequired
                          onChange={(val) => setCity(val)}
                        />
                      </Layout.FormGroup>
                      <Layout.FormGroup
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 20,
                          marginTop: 10,
                        }}>
                        <SociollaText>Is Active</SociollaText>
                        <SociollaCheckbox label="" name="is_active" />
                      </Layout.FormGroup>
                      <Layout.FormGroup>
                        <View
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                          }}>
                          <SociollaText
                            style={{
                              color: Colors.black3,
                            }}>
                            Location
                          </SociollaText>
                          <View style={{ marginTop: 12 }}>
                            <SociollaMapPicker
                              name="lat_long"
                              disabled={true}
                              hideInput={true}
                              onCurrentLocation={
                                detailData?.lat_long?.latitude &&
                                detailData?.lat_long?.longitude
                                  ? detailData.lat_long
                                  : onCurrentLocation
                              }
                              onChangeAddress={(val) => {
                                if (states?.length && val.state) {
                                  const st = states.find(
                                    (a) =>
                                      a.name &&
                                      findMatchValue(a.name, val.state),
                                  );
                                  if (st) {
                                    setDetailData((prev) => ({
                                      ...prev,
                                      lat_long: {
                                        ...prev.lat_long,
                                        latitude: st.latitude,
                                        longitude: st.longitude,
                                      },
                                    }));
                                  }
                                }
                              }}
                              containerStyle={{ width: '100%' }}
                            />
                          </View>
                        </View>
                      </Layout.FormGroup>
                    </View>

                    {/* TAX IDENFITICATION */}
                    <View style={{ width: '100%' }}>
                      <TitleSubSection
                        title={'TAX IDENTIFICATION'}
                        style={{ marginBottom: 10 }}
                      />
                      <View style={styles.rowForm}>
                        <Layout.FormGroup>
                          <SociollaInput
                            name="tax_name"
                            label="Name"
                            isRequired
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SociollaInput
                            name="tax_number"
                            label="Number"
                            isRequired
                            isNPWP
                            isNumeric
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SociollaSelectBox
                            label="Taxable Entrepreneur"
                            options={PKP_TYPES}
                            name="tax_is_pkp"
                            trackBy="value"
                            labelKey="name"
                            isRequired
                            value={{
                              name: values['tax_is_pkp']?.value
                                ? 'Taxable'
                                : 'Non Taxable',
                              value: values['tax_is_pkp']?.value ? true : false,
                            }}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SociollaSelectBox
                            label="WHT (Corporate/Personal)"
                            options={WHT}
                            name="tax_wht"
                            trackBy="value"
                            labelKey="label"
                            isRequired
                            value={
                              values.tax_wht
                                ? WHT.find(
                                    (el) => el.value == values.tax_wht.value,
                                  )
                                : ''
                            }
                          />
                        </Layout.FormGroup>
                      </View>
                    </View>
                  </Layout.FormWrapper>
                </ScrollView>
              </Layout.Wrapper>
              {!isWeb && (isEdit ? !getOutletDetail?.loading : true) ? (
                <Layout.ButtonSticky
                  show={props?.route?.name == 'SubdistributorFormScreen'}
                  route={props?.route}
                  buttons={[
                    {
                      title: 'Cancel',
                      type: 'default',
                      onPress: () => {
                        goBack();
                      },
                    },
                    {
                      title: isEdit ? 'Save' : 'Add',
                      type: 'primary',
                      onPress: (evt) => {
                        validateForm().then(() => {
                          handleSubmit(evt);
                        });
                      },
                    },
                  ]}
                />
              ) : (
                false
              )}
            </>
          )}
        </Formik>
      ) : (
        false
      )}
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  rowForm: { width: isWeb ? 'calc(50% - 20px)' : '100%' },
});

export default SubdistributorFormScreen;
